import { makeAutoObservable, observable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import i18n from 'i18n';
import {
  getIntegrationSettingRequest,
  getIntegrationSettingsRequest,
  getOperatorsListRequest,
  resetIntegrationSettingRequest,
  updateIntegrationSettingRequest
} from 'services/requestAgent';
import { showErrorNotification } from 'services/notificationUtils';

export const INTEGRATION_SETTING_SOURCE = {
  DATABASE: 'DATABASE',
  DEFAULTS: 'DEFAULTS'
};

const ERROR_CODE = {
  ARGUMENT_NOT_VALID: 'ARGUMENT_NOT_VALID',
  VERSION_MISMATCH: 'VERSION_MISMATCH'
};

const toListItem = ({ provider, settingKey, description, version, source, updatedAt, updatedBy }) => ({
  provider,
  settingKey,
  description,
  version,
  source,
  updatedAt,
  updatedBy
});

class IntegrationSettingsStore {
  isInitialized = false;
  isLoading = false;
  isSettingLoading = false;
  isSaving = false;
  isVersionConflict = false;
  settings = [];
  setting = null;
  draftValue = null;
  // Validation errors of the last save, keyed by the path inside the value: "levels[0].levelName"
  serverErrors = {};
  operators = [];
  // Set when the opened setting could not be loaded (unknown key, no access), to tell it apart from loading
  settingError = null;
  error = null;

  constructor() {
    // Values are replaced as a whole on every edit, so plain JS objects are enough and compare with isEqual
    makeAutoObservable(this, {
      settings: observable.ref,
      setting: observable.ref,
      draftValue: observable.ref,
      serverErrors: observable.ref,
      operators: observable.ref
    });
  }

  get isDraftChanged() {
    return Boolean(this.setting) && !isEqual(this.draftValue, this.setting.value);
  }

  getOperatorName = (operatorId) => {
    if (operatorId === null || operatorId === undefined) return null;
    return this.operators.find(({ id }) => id === operatorId)?.name || `#${operatorId}`;
  };

  isCurrentSetting = (provider, settingKey) =>
    this.setting?.provider === provider && this.setting?.settingKey === settingKey;

  setDraftValue = (value) => {
    this.draftValue = value;
    this.serverErrors = {};
  };

  discardChanges = () => {
    if (!this.setting) return;
    this.draftValue = cloneDeep(this.setting.value);
    this.serverErrors = {};
  };

  applySetting = (setting) => {
    this.setting = setting;
    this.draftValue = cloneDeep(setting.value);
    this.serverErrors = {};
    this.isVersionConflict = false;
    this.settings = this.settings.map((item) =>
      item.provider === setting.provider && item.settingKey === setting.settingKey ? toListItem(setting) : item
    );
  };

  // Only needed to show who changed a setting; the page works without it
  loadOperators = async () => {
    if (this.operators.length) return;
    try {
      const operators = await getOperatorsListRequest();
      runInAction(() => {
        this.operators = Array.isArray(operators) ? operators : [];
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    }
  };

  getIntegrationSettings = async () => {
    this.isLoading = true;
    this.error = null;
    try {
      const [settings] = await Promise.all([getIntegrationSettingsRequest(), this.loadOperators()]);
      runInAction(() => {
        this.settings = Array.isArray(settings) ? settings : [];
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isInitialized = true;
        this.isLoading = false;
      });
    }
  };

  getIntegrationSetting = async (provider, settingKey) => {
    this.isSettingLoading = true;
    this.error = null;
    this.settingError = null;
    this.setting = null;
    this.draftValue = null;
    this.serverErrors = {};
    this.isVersionConflict = false;
    try {
      const [setting] = await Promise.all([getIntegrationSettingRequest(provider, settingKey), this.loadOperators()]);
      runInAction(() => {
        this.applySetting(setting);
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.settingError = err;
      });
    } finally {
      runInAction(() => {
        this.isSettingLoading = false;
      });
    }
  };

  updateIntegrationSetting = async () => {
    if (!this.setting) return;

    const { provider, settingKey, version } = this.setting;
    this.isSaving = true;
    this.serverErrors = {};
    try {
      const setting = await updateIntegrationSettingRequest(provider, settingKey, this.draftValue, version);
      runInAction(() => {
        this.applySetting(setting);
      });
      toast.success(i18n.getMessage('integrationSettingsStore.updateIntegrationSetting.success'));
    } catch (err) {
      runInAction(() => {
        this.error = err;
        if (err?.code === ERROR_CODE.VERSION_MISMATCH) {
          this.isVersionConflict = true;
          toast.error(i18n.getMessage('integrationSettingsStore.updateIntegrationSetting.versionMismatch'));
        } else if (err?.code === ERROR_CODE.ARGUMENT_NOT_VALID && Array.isArray(err.details)) {
          this.serverErrors = err.details.reduce((result, { field, message }) => {
            const path = field || '';
            if (!(path in result)) {
              result[path] = message;
            }
            return result;
          }, {});
          toast.error(i18n.getMessage('integrationSettingsStore.updateIntegrationSetting.invalidValue'));
        } else {
          showErrorNotification(err?.status, err?.message);
        }
      });
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  resetIntegrationSetting = async () => {
    if (!this.setting) return;

    const { provider, settingKey } = this.setting;
    this.isSaving = true;
    const toastPromise = toast.promise(resetIntegrationSettingRequest(provider, settingKey), {
      pending: i18n.getMessage('integrationSettingsStore.resetIntegrationSetting.toastPromise.pending'),
      success: i18n.getMessage('integrationSettingsStore.resetIntegrationSetting.toastPromise.success')
    });
    try {
      const setting = await toastPromise;
      runInAction(() => {
        this.applySetting(setting);
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  resetIntegrationSettingsStore = () => {
    this.isInitialized = false;
    this.isLoading = false;
    this.isSettingLoading = false;
    this.isSaving = false;
    this.isVersionConflict = false;
    this.settings = [];
    this.setting = null;
    this.draftValue = null;
    this.serverErrors = {};
    this.settingError = null;
    this.error = null;
  };
}

export default new IntegrationSettingsStore();
